import { IdentityProvider } from "Api/Api";
import { getAppleSsoContext } from "Components/Auth/Apple/getAppleSsoContext";
import { openAppleAuthInBrowser } from "Components/Auth/Apple/openAppleAuthInBrowser";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { AppleFlow } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { signInSsoAsync } from "State/Auth/SignIn/SignInSsoState";
import { Resources, useResource } from "Translations/Resources";
import { isAndroid, isIOS } from "Utils/Platform/PlatformUtils";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import AppleSignin from "react-apple-signin-auth";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import styled from "styled-components";

// @capacitor-community/apple-sign-in forwards only `error.localizedDescription`
// (the underlying ASAuthorizationError code 1001 is dropped), so we have to
// match the device-locale cancel string. "zruš" covers both Czech (zrušil/
// zrušeno) and Slovak (zrušiť/zrušený) prefixes.
const APPLE_CANCEL_KEYWORDS = ["cancel", "zruš", "hủy"];

const isAppleSignInCancellation = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const message = (error as { message?: string }).message?.toLowerCase() ?? "";
  return APPLE_CANCEL_KEYWORDS.some((keyword) => message.includes(keyword));
};

type Props = {
  isLoading: boolean;
  affiliateCode?: string;
  /**
   * When set, the authorization code is handed here instead of signing the user in. Used by
   * link-to-customer, where the Apple sign-in only proves ownership of an existing account.
   */
  onAuthorized?: (code: string, appleFlow: AppleFlow) => void;
};

const StyledNativeButton = styled.button`
  cursor: pointer;
  transition: background-color 0.3s, box-shadow 0.3s;
  padding: 15px 16px;
  height: 46px;
  border: none;
  border-radius: 3px;
  box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 1px 1px rgba(0, 0, 0, 0.25);

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: "Outfit", sans-serif;
  color: #ffffff;
  background-color: #000000;

  &:hover {
    box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.25);
  }

  &:active {
    background-color: #1a1a1a;
  }

  &:focus {
    outline: none;
    box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.25),
      0 0 0 3px rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    filter: grayscale(100%);
    background-color: #3a3a3a;
    cursor: not-allowed;
  }
`;

const AppleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282z" />
  </svg>
);

export const AppleButton = ({
  isLoading,
  affiliateCode: affiliateCodeProp,
  onAuthorized,
}: Props) => {
  const clientId = import.meta.env.VITE_APPLE_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_APPLE_REDIRECT_URL;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useResource();
  const user = useLoggedInUser();

  const handleAuthorizationCode = (token: string, appleFlow: AppleFlow) => {
    if (!token) {
      return;
    }
    if (onAuthorized) {
      onAuthorized(token, appleFlow);
      return;
    }

    dispatch(
      signInSsoAsync.request({
        model: {
          token,
          identityProvider: IdentityProvider.Apple,
          ...getAppleSsoContext(user, affiliateCodeProp),
          appleFlow,
        },
        navigate,
      }),
    );
  };

  const nonce = (Math.random() + 1).toString(36).substring(7);
  const label = t(Resources.Auth.SSO.Apple.ButtonLabel);

  if (isIOS()) {
    const handleIosSignIn = async () => {
      try {
        const response = await SignInWithApple.authorize({
          clientId,
          redirectURI: redirectUri,
          scopes: "email name",
          state: "state",
          nonce,
        });
        handleAuthorizationCode(
          response.response.authorizationCode,
          AppleFlow.Native,
        );
      } catch (error) {
        if (isAppleSignInCancellation(error)) {
          return;
        }
        toast.error(t(Resources.Auth.SSO.Apple.SignInFailed));
      }
    };

    return (
      <StyledNativeButton
        type="button"
        disabled={isLoading}
        onClick={handleIosSignIn}
      >
        <AppleIcon />
        <span>{label}</span>
      </StyledNativeButton>
    );
  }

  if (isAndroid()) {
    // Android shell: no native Apple SDK exists and the web popup cannot return into a
    // WebView, so the sign-in runs as a browser redirect through the API's apple-callback
    // (see openAppleAuthInBrowser). The result lands on AppleRedirectPage, not here, which
    // is also why link-to-customer (onAuthorized) is not offered on Android — the form shows
    // a "use a browser" message instead.
    if (onAuthorized) {
      return null;
    }

    // The sign-up context is captured NOW and travels with the stored nonce: the return
    // lands on a freshly loaded page where the Redux user and the affiliate prop are gone.
    const handleAndroidSignIn = () => {
      openAppleAuthInBrowser(getAppleSsoContext(user, affiliateCodeProp)).catch(
        () => {
          toast.error(t(Resources.Auth.SSO.Apple.SignInFailed));
        },
      );
    };

    return (
      <StyledNativeButton
        type="button"
        disabled={isLoading}
        onClick={handleAndroidSignIn}
      >
        <AppleIcon />
        <span>{label}</span>
      </StyledNativeButton>
    );
  }

  const className = isLoading
    ? "apple-auth-btn apple-auth-btn-disabled"
    : "apple-auth-btn";

  return (
    <AppleSignin
      authOptions={{
        clientId,
        scope: "email name",
        redirectURI: redirectUri,
        state: "state",
        nonce,
        usePopup: true,
      }}
      uiType="dark"
      className={className}
      noDefaultStyle={false}
      buttonExtraChildren={label}
      onSuccess={(response: any) =>
        handleAuthorizationCode(response?.authorization?.code, AppleFlow.Web)
      }
      onError={(error: any) => console.log(error)}
      skipScript={false}
    />
  );
};
